import { useState, useEffect, useCallback, createContext, useContext } from "react";
import React from "react";

export type ContentTheme = "dark" | "light" | "auto";

const STORAGE_KEY = "content-theme";

function getSystemTheme(): "dark" | "light" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

interface ContentThemeContextValue {
  theme: ContentTheme;
  setTheme: (t: ContentTheme) => void;
  resolved: "dark" | "light";
  cycleTheme: () => void;
}

const ContentThemeContext = createContext<ContentThemeContextValue>({
  theme: "dark",
  setTheme: () => {},
  resolved: "dark",
  cycleTheme: () => {},
});

export const ContentThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ContentTheme>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem(STORAGE_KEY) as ContentTheme) || "dark";
  });

  const [systemTheme, setSystemTheme] = useState<"dark" | "light">(getSystemTheme);
  const resolved = theme === "auto" ? systemTheme : theme;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => setSystemTheme(mq.matches ? "dark" : "light");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const cycleTheme = useCallback(() => {
    setTheme((prev) => {
      if (prev === "dark") return "light";
      if (prev === "light") return "auto";
      return "dark";
    });
  }, []);

  return React.createElement(ContentThemeContext.Provider, { value: { theme, setTheme, resolved, cycleTheme } }, children);
};

export function useContentTheme() {
  return useContext(ContentThemeContext);
}
